import { faRedo } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { ConnectionStatus, editorStore } from '../../stores/EditorStore';
import { errorStore } from '../../stores/ErrorStore';
import { imageStore } from '../../stores/ImageStore';
import { networkDiagnosticsStore } from '../../stores/NetworkDiagnosticsStore';
import { sharedStore } from '../../stores/SharedStore';
import { undoStore } from '../../stores/undo/UndoStore';
import { NetworkDiagnostics } from '../debug/NetworkDiagnostics';
import { AdamaraLogo } from '../shared/AdamaraLogo';
import { Heading1Base } from '../shared/Heading';
import { LoadingBarBlock } from '../shared/LoadingBar';
import { CenterContainer, Overlay, PopupWindow } from '../shared/PopupComponents';
import { LoadingHeaderRightEditor } from './Header';
const ColumNotifications = styled.div.withConfig({
  displayName: "EditorNotifications__ColumNotifications",
  componentId: "sc-1h2v9te-0"
})(["display:flex;align-items:center;justify-content:center;flex-direction:column;flex-wrap:wrap;"]);
const Title = Heading1Base;
const TextCenter = styled.div.withConfig({
  displayName: "EditorNotifications__TextCenter",
  componentId: "sc-1h2v9te-1"
})(["text-align:center;"]);
const ErrorPopupWindow = styled(PopupWindow).withConfig({
  displayName: "EditorNotifications__ErrorPopupWindow",
  componentId: "sc-1h2v9te-2"
})(["background:#bb5050;"]);
const ErrorList = styled.ul.withConfig({
  displayName: "EditorNotifications__ErrorList",
  componentId: "sc-1h2v9te-3"
})(["max-height:80vh;"]);
export const EditorNotificationsErrorsWithWithContainer = () => {
  return /*#__PURE__*/React.createElement(CenterContainer, null, /*#__PURE__*/React.createElement(EditorNotificationsErrors, null));
};
const EditorNotificationsErrors = observer(() => {
  const {
    t
  } = useTranslation();
  return /*#__PURE__*/React.createElement(ErrorPopupWindow, null, /*#__PURE__*/React.createElement(Title, null, t("editor.errors")), /*#__PURE__*/React.createElement(ErrorList, null, errorStore.errors.map((error, index) => /*#__PURE__*/React.createElement("li", {
    key: index
  }, error.translate(t)))), /*#__PURE__*/React.createElement("button", {
    onClick: errorStore.clearErrors
  }, t("editor.ok")));
});
export const EditorNotifications = observer(({
  isConnectedAndReadyCallback,
  AdditionalConnectionStatusDisplay,
  LoadingHeader
}) => {
  const {
    reloadNecessaryReasonTranslationKey,
    serverWasShutDown
  } = editorStore;
  const {
    hasErrors
  } = errorStore;
  const {
    showWaitingForServerOverlay
  } = undoStore;
  const isConnectedAndReady = isConnectedAndReadyCallback();
  const {
    t
  } = useTranslation();
  const showConnectionStatus = !isConnectedAndReady;
  if (!showConnectionStatus && !hasErrors && !reloadNecessaryReasonTranslationKey && !serverWasShutDown && !showWaitingForServerOverlay && !networkDiagnosticsStore.showBecauseOfUserRequest) return null;

  if (serverWasShutDown) {
    return /*#__PURE__*/React.createElement(Overlay, null, /*#__PURE__*/React.createElement(CenterContainer, null, /*#__PURE__*/React.createElement(ErrorPopupWindow, null, /*#__PURE__*/React.createElement("div", null, t("editor.server_was_shutdown")))));
  }

  return /*#__PURE__*/React.createElement(Overlay, {
    onClick: () => networkDiagnosticsStore.setShowBecauseOfUserRequest(false)
  }, /*#__PURE__*/React.createElement(CenterContainer, null, /*#__PURE__*/React.createElement(ColumNotifications, null, showConnectionStatus && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(PopupWindow, null, /*#__PURE__*/React.createElement(TextCenter, null, /*#__PURE__*/React.createElement(AdamaraLogo, null), /*#__PURE__*/React.createElement(Title, null, t("editor.socketio_connection_status_" + ConnectionStatus[editorStore.connectionStatus])), /*#__PURE__*/React.createElement(AdditionalConnectionStatusDisplay, null), /*#__PURE__*/React.createElement("hr", null), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("button", {
    onClick: e => {
      e.stopPropagation();
      networkDiagnosticsStore.toggleShowBecauseOfUserRequest();
    }
  }, t("editor.network_diagnostics"))))), /*#__PURE__*/React.createElement(LoadingHeader, null)), showWaitingForServerOverlay && /*#__PURE__*/React.createElement(PopupWindow, null, t("editor.waiting_for_server")), hasErrors && /*#__PURE__*/React.createElement(EditorNotificationsErrors, null), reloadNecessaryReasonTranslationKey && /*#__PURE__*/React.createElement(ErrorPopupWindow, null, /*#__PURE__*/React.createElement("div", null, t(reloadNecessaryReasonTranslationKey)), /*#__PURE__*/React.createElement("button", {
    onClick: () => location.reload()
  }, /*#__PURE__*/React.createElement(FontAwesomeIcon, {
    icon: faRedo
  }), "\xA0", t("editor.reload_necessary_button")))), networkDiagnosticsStore.showBecauseOfUserRequest && /*#__PURE__*/React.createElement(NetworkDiagnostics, null)));
});
export const EditorAndGameLoadingBars = observer(() => {
  const {
    t
  } = useTranslation();
  if (editorStore.connectionStatus !== ConnectionStatus.Connected) return null;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("hr", null), /*#__PURE__*/React.createElement(LoadingBarBlock, {
    label: t("editor.loading_mixed_data"),
    percentage100: Math.floor(editorStore.initializationPercentage * 100)
  }), /*#__PURE__*/React.createElement(LoadingBarBlock, {
    label: t("editor.loading_images"),
    percentage100: Math.floor(imageStore.loadingPercentage * 100)
  }), /*#__PURE__*/React.createElement(LoadingBarBlock, {
    label: t("editor.loading_tile_asset_data"),
    percentage100: Math.floor(sharedStore.tileAssetDataLoadingPercentage * 100)
  }), /*#__PURE__*/React.createElement(LoadingBarBlock, {
    label: t("editor.loading_animation_assets"),
    percentage100: Math.floor(sharedStore.animationAssetsLoadingPercentage * 100)
  }), /*#__PURE__*/React.createElement(LoadingBarBlock, {
    label: t("editor.loading_action_trees"),
    percentage100: Math.floor(sharedStore.actionTreeLoadingPercentage * 100)
  }));
});
export const EditorNotificationsEditorAndGame = () => {
  return /*#__PURE__*/React.createElement(EditorNotifications, {
    isConnectedAndReadyCallback: () => editorStore.isConnectedAndReady,
    AdditionalConnectionStatusDisplay: EditorAndGameLoadingBars,
    LoadingHeader: LoadingHeaderRightEditor
  });
};